/**
 * Phase 5Z.19o — Disaster Recovery / Full System Backup panel.
 *
 * Admin-only. Calls /api/admin/full-backup/* endpoints. Every action
 * (create, download, delete) is gated by a fixed shared password
 * (Damjan04042017!) plus the user's admin role.
 */
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ShieldCheck, Download, Trash2, Loader2, AlertTriangle, Calendar,
  Save, RefreshCw, Lock,
} from "lucide-react";

const WEEKDAY_LABELS_DE = ["Montag", "Dienstag", "Mittwoch", "Donnerstag",
                           "Freitag", "Samstag", "Sonntag"];

function fmtBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0; while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-AT"); } catch { return iso; }
}

export default function DisasterRecoveryPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/full-backup/list");
      setItems(data.items || []);
      setConfig(data.config || null);
    } catch (e) {
      // Non-admins get 403 — silently render the lockout state
      setItems([]);
      setConfig(null);
    } finally { setLoading(false); }
  }
  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin]);

  async function promptPassword(verb) {
    const pw = window.prompt(
      `Backup-Passwort eingeben um fortzufahren (${verb}).\n\n` +
      "⚠️ ACHTUNG: Diese Aktion exportiert sensible Daten inkl. " +
      "Datenbank-Inhalten und Umgebungsvariablen. Bewahre die ZIP " +
      "sicher auf — sie enthält Passwort-Hashes, API-Keys und alle " +
      "Geschäftsdaten."
    );
    return pw || null;
  }

  async function createNow() {
    if (!isAdmin) return;
    const pw = await promptPassword("erstellen");
    if (!pw) return;
    setCreating(true);
    try {
      const { data } = await api.post("/admin/full-backup/create", { password: pw });
      toast.success(`Backup erstellt — ${fmtBytes(data.size_bytes)}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erstellung fehlgeschlagen");
    } finally { setCreating(false); }
  }

  async function downloadOne(id) {
    const pw = await promptPassword("herunterladen");
    if (!pw) return;
    try {
      const res = await api.post(`/admin/full-backup/download/${encodeURIComponent(id)}`,
        { password: pw }, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = id;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download gestartet");
    } catch (e) {
      const msg = e.response?.data?.detail || (e.response?.status === 401 ? "Falsches Passwort" : "Download fehlgeschlagen");
      toast.error(msg);
    }
  }

  async function deleteOne(id) {
    if (!window.confirm(`Backup "${id}" wirklich löschen?`)) return;
    const pw = await promptPassword("löschen");
    if (!pw) return;
    try {
      await api.post(`/admin/full-backup/${encodeURIComponent(id)}/delete`, { password: pw });
      toast.success("Backup gelöscht");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  }

  async function saveConfig(patch) {
    if (!config) return;
    setSavingConfig(true);
    try {
      const next = { ...config, ...patch };
      const { data } = await api.put("/admin/full-backup/config", {
        weekly_enabled: !!next.weekly_enabled,
        weekday: Number(next.weekday) || 0,
        hour: Number(next.hour) || 3,
        keep_last: Number(next.keep_last) || 4,
      });
      setConfig(data);
      toast.success("Einstellungen gespeichert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSavingConfig(false); }
  }

  if (!isAdmin) {
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded p-6 flex items-start gap-3" data-testid="dr-locked">
        <Lock className="w-5 h-5 text-zinc-500 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-display text-zinc-50 font-semibold">Disaster-Recovery</h3>
          <p className="text-xs text-zinc-500 mt-1">
            Nur Administratoren können vollständige System-Backups erstellen und herunterladen.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 border border-rose-500/30 rounded p-6" data-testid="dr-panel">
      <div className="flex items-start gap-3 mb-4">
        <ShieldCheck className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-zinc-50 font-semibold">
            Full System Backup / Disaster Recovery Export
          </h3>
          <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
            Erstellt eine vollständige ZIP-Sicherung der gesamten Anwendung —
            Quellcode (Backend + Frontend), MongoDB-Dump, Umgebungs-Dateien,
            Restore-Skripte für Linux & Windows und eine ausführliche Anleitung.
            Damit kann die Plattform jederzeit auf einer beliebigen Maschine
            außerhalb von Emergent wiederhergestellt werden.
          </p>
        </div>
      </div>

      <div className="bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2.5 mb-4 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-rose-200/90">
          Die ZIP enthält sensible Daten (Datenbank-Inhalte, gehashte Passwörter,
          API-Keys, IBANs). Speichere sie ausschließlich an einem sicheren Ort
          (verschlüsseltes Laufwerk, Passwort-Manager-Vault).
          Jede Aktion erfordert das festgelegte Backup-Passwort.
        </div>
      </div>

      <button
        type="button"
        onClick={createNow}
        disabled={creating}
        data-testid="dr-create-btn"
        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white rounded transition-colors"
      >
        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        Download Full System Backup
      </button>

      {/* Backup list */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-zinc-200">Verfügbare Backups</h4>
          <button onClick={load} title="Neu laden" className="p-1 text-zinc-500 hover:text-zinc-200">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
        {loading ? (
          <div className="text-xs text-zinc-500 py-4">Lade…</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-zinc-500 py-4 italic" data-testid="dr-empty">Noch keine Full-Backups erstellt.</div>
        ) : (
          <div className="border border-zinc-800 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="px-3 py-2 text-left">Datei</th>
                  <th className="px-3 py-2 text-right">Größe</th>
                  <th className="px-3 py-2 text-left">Erstellt</th>
                  <th className="px-3 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {items.map(it => (
                  <tr key={it.id} data-testid={`dr-row-${it.id}`} className="hover:bg-zinc-950/40">
                    <td className="px-3 py-2 font-mono text-xs text-zinc-200 truncate max-w-[280px]">{it.filename}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs text-zinc-300 tabular-nums">{fmtBytes(it.size_bytes)}</td>
                    <td className="px-3 py-2 text-xs text-zinc-400">{fmtDate(it.created_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => downloadOne(it.id)} title="Download"
                        data-testid={`dr-dl-${it.id}`}
                        className="p-1.5 text-blue-400 hover:bg-blue-500/10 rounded">
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deleteOne(it.id)} title="Löschen"
                        data-testid={`dr-del-${it.id}`}
                        className="p-1.5 text-rose-400 hover:bg-rose-500/10 rounded ml-1">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Weekly schedule */}
      {config && (
        <div className="mt-6 border-t border-zinc-800 pt-5">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-amber-400" />
            <h4 className="text-sm font-semibold text-zinc-200">Wöchentliche automatische Sicherung</h4>
          </div>
          <p className="text-[11px] text-zinc-500 mb-3">
            Wenn aktiviert wird wöchentlich automatisch ein neues Full-System-Backup erstellt
            und auf dem Server abgelegt (zum späteren Download über diese Seite).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer" data-testid="dr-cfg-enabled">
              <input
                type="checkbox"
                checked={!!config.weekly_enabled}
                onChange={(e) => setConfig({ ...config, weekly_enabled: e.target.checked })}
                className="w-4 h-4 accent-amber-500"
              />
              Aktiviert
            </label>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Wochentag</label>
              <select
                value={config.weekday}
                onChange={(e) => setConfig({ ...config, weekday: Number(e.target.value) })}
                data-testid="dr-cfg-weekday"
                className="w-full px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100"
              >
                {WEEKDAY_LABELS_DE.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Uhrzeit (Stunde)</label>
              <input
                type="number" min="0" max="23"
                value={config.hour}
                onChange={(e) => setConfig({ ...config, hour: Number(e.target.value) })}
                data-testid="dr-cfg-hour"
                className="w-full px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 font-mono"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Behalte letzte N</label>
              <input
                type="number" min="1" max="20"
                value={config.keep_last}
                onChange={(e) => setConfig({ ...config, keep_last: Number(e.target.value) })}
                data-testid="dr-cfg-keep"
                className="w-full px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 font-mono"
              />
            </div>
          </div>
          <button
            onClick={() => saveConfig({})}
            disabled={savingConfig}
            data-testid="dr-cfg-save"
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 rounded"
          >
            {savingConfig ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Zeitplan speichern
          </button>
          {config.last_auto_run && (
            <div className="mt-2 text-[10px] text-zinc-500">
              Letzter automatischer Lauf: <span className="font-mono text-zinc-400">{config.last_auto_run}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
